import { createContext, type ReactNode, useContext, useMemo } from "react";
import {
  type CreateTableOverlayConfig,
  createTableOverlay,
} from "@/components/discovery/table-overlays/createTableOverlay";

export interface BriefingsTableOverlayState {
  error: string | null;
  isFetching: boolean;
  totalCount: number;
  isInitialLoading: boolean;
  isFiltered: boolean;
  onClearFilters?: () => void;
  clearError: () => void;
}

const BriefingsTableOverlayContext =
  createContext<BriefingsTableOverlayState | null>(null);

const useBriefingsTableOverlayContext = (): BriefingsTableOverlayState => {
  const value = useContext(BriefingsTableOverlayContext);
  if (!value) {
    throw new Error(
      "Briefings table overlay hooks must be used within BriefingsTableOverlayProvider",
    );
  }
  return value;
};

const useBriefingsTableState: CreateTableOverlayConfig["useTableState"] =
  () => {
    const { error, isFetching, totalCount, isInitialLoading } =
      useBriefingsTableOverlayContext();
    return { error, isFetching, totalCount, isInitialLoading };
  };

const useBriefingsNoRowsActions: CreateTableOverlayConfig["useNoRowsActions"] =
  () => {
    const { isFiltered, onClearFilters } = useBriefingsTableOverlayContext();
    return {
      isAppliedFilters: isFiltered,
      onReset: onClearFilters,
    };
  };

const useBriefingsClearError: CreateTableOverlayConfig["useClearError"] =
  () => {
    const { clearError } = useBriefingsTableOverlayContext();
    return clearError;
  };

const briefingsTableOverlayConfig: Omit<
  CreateTableOverlayConfig,
  "noRowsMessageWithFilters"
> = {
  useTableState: useBriefingsTableState,
  noRowsMessageWithoutFilters: "No briefings to show",
  useNoRowsActions: useBriefingsNoRowsActions,
  errorTitle: "Failed to load briefings",
  useClearError: useBriefingsClearError,
};

/** Creates the unified no-rows / error overlay for the briefings table. */
export const createBriefingsTableUnifiedOverlay = (
  noRowsMessageWithFilters: string,
  onRetry?: () => void,
) =>
  createTableOverlay(
    { ...briefingsTableOverlayConfig, noRowsMessageWithFilters },
    onRetry,
  );

interface BriefingsTableOverlayProviderProps {
  state: BriefingsTableOverlayState;
  children: ReactNode;
}

export const BriefingsTableOverlayProvider = ({
  state,
  children,
}: BriefingsTableOverlayProviderProps) => {
  const overlayState = useMemo(() => state, [state]);
  return (
    <BriefingsTableOverlayContext.Provider value={overlayState}>
      {children}
    </BriefingsTableOverlayContext.Provider>
  );
};
